import catalog from '@shared/specs/meta-creative-v25.json';

// Which Meta placements a set of uploaded aspect ratios can serve natively.
//
// The browser's half of the table the worker holds in
// `shared/src/types/meta_placement.rs`. Neither generates the other; the guard
// test in `shared/src/types/meta_preview.rs` fails the build if they drift, and
// `preview-placements.test.ts` holds the browser side of the same contract.
//
// A placement names its eligible profiles in PREFERENCE order and is served by
// the first one present. Only `facebook_feed` has a second entry — Meta's Ads
// Guide lists 4:5 as the Facebook Feed spec, so a creative without a 1:1 still
// reaches Feed.

export type MetaAssetProfileId = 'meta_sq' | 'meta_fs' | 'meta_story';

export interface MetaPlacement {
  key: string;
  label: string;
  asset_profiles: string[];
}

const PLACEMENTS = catalog.placements as MetaPlacement[];

/** The wizard's slot keys (`sq`/`fs`/`story`) are the profile ids without the prefix. */
export function profileIdForSlot(slotKey: string): MetaAssetProfileId {
  return `meta_${slotKey}` as MetaAssetProfileId;
}

/** Placements the given profiles can serve, in catalog order. */
export function enabledPlacements(profiles: readonly string[]): MetaPlacement[] {
  const present = new Set(profiles);
  return PLACEMENTS.filter((placement) =>
    placement.asset_profiles.some((profile) => present.has(profile)));
}

export function disabledPlacements(profiles: readonly string[]): MetaPlacement[] {
  const enabled = new Set(enabledPlacements(profiles).map((placement) => placement.key));
  return PLACEMENTS.filter((placement) => !enabled.has(placement.key));
}

/**
 * Placements this profile serves given everything the creative carries — i.e.
 * the ones where it is the preferred profile present. A 4:5 covers Facebook
 * Feed only when there is no 1:1.
 */
export function placementsServedBy(
  profile: string,
  allProfiles: readonly string[],
): MetaPlacement[] {
  const present = new Set(allProfiles);
  return PLACEMENTS.filter((placement) =>
    placement.asset_profiles.find((candidate) => present.has(candidate)) === profile);
}

export const TOTAL_PLACEMENTS = PLACEMENTS.length;

/**
 * The placements a whole campaign can run, which is the INTERSECTION over its
 * creatives — one Meta ad set holds every creative for its audience and
 * placements are an ad-set property, so anything wider would put an ad in front
 * of a position its own creative has no asset for.
 */
export function campaignPlacements(creativeProfiles: readonly (readonly string[])[]): MetaPlacement[] {
  if (creativeProfiles.length === 0) return [];
  return creativeProfiles
    .map((profiles) => enabledPlacements(profiles))
    .reduce((left, right) => {
      const keep = new Set(right.map((placement) => placement.key));
      return left.filter((placement) => keep.has(placement.key));
    });
}
